import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import { statusTone } from '../../theme';
import { formatDate } from '../../utils/format';
import { formatPriorityLabel } from '../../utils/projectStatus';
import Section from '../ui/Section';
import KeyValueList, { KeyValueItem } from '../ui/KeyValueList';
import PriorityFlag from '../Projects/PriorityFlag';
import { describeDeadline } from './workflow';

interface BriefTabProps {
  description?: string | null;
  comments?: string | null;
  targetAudience?: string | null;
  desiredStyle?: string | null;
  musicPreference?: string | null;
  colorPreference?: string | null;
  brandGuidelines?: string | null;
  deliverables?: string[] | string | null;
  referenceLinks: string[];
  deadline?: string | null;
  priority?: string | null;
  estimatedComplexity?: string | null;
  createdAt?: string | null;
  finished: boolean;
  creatorName: string;
}

const text = (value?: string | null) => (typeof value === 'string' && value.trim() ? value.trim() : null);

const multiline = (value: string | null) =>
  value ? <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>{value}</Box> : null;

/**
 * The creator's brief: what the video is for, how it should feel, and when
 * it is due. The New project form doesn't ask for notes or references, so
 * those panels only appear on projects that have them (older ones do).
 */
const BriefTab: React.FC<BriefTabProps> = ({
  description,
  comments,
  targetAudience,
  desiredStyle,
  musicPreference,
  colorPreference,
  brandGuidelines,
  deliverables,
  referenceLinks,
  deadline,
  priority,
  estimatedComplexity,
  createdAt,
  finished,
  creatorName,
}) => {
  const deliverableList = Array.isArray(deliverables)
    ? deliverables.filter((item) => typeof item === 'string' && item.trim())
    : text(deliverables as string | null)
      ? [String(deliverables).trim()]
      : [];
  const references = referenceLinks.filter((link) => typeof link === 'string' && link.trim());
  const notes = text(comments);
  const brief = text(description);
  const due = describeDeadline(deadline, new Date(), finished);
  const flagged = ['HIGH', 'URGENT'].includes(String(priority || '').toUpperCase());

  const direction: KeyValueItem[] = [
    { label: 'Audience', value: text(targetAudience) },
    { label: 'Style', value: text(desiredStyle) },
    { label: 'Music', value: text(musicPreference) },
    { label: 'Colour', value: text(colorPreference) },
    { label: 'Brand guidelines', value: multiline(text(brandGuidelines)), fullWidth: true },
    { label: 'Deliverables', value: deliverableList.length > 0 ? deliverableList.join(' · ') : null, fullWidth: true },
  ];

  const schedule: KeyValueItem[] = [
    {
      label: 'Deadline',
      value: deadline && formatDate(deadline) ? (
        <Box component="span">
          {formatDate(deadline)}
          {due && (
            <Box
              component="span"
              sx={{
                ml: 1,
                fontWeight: due.tone === 'error' || due.tone === 'warning' ? 600 : 400,
                color: due.tone === 'error' ? statusTone.problem.fg : due.tone === 'warning' ? statusTone.attention.fg : 'text.secondary',
              }}
            >
              {due.relative}
            </Box>
          )}
        </Box>
      ) : 'No deadline',
    },
    { label: 'Priority', value: flagged ? <PriorityFlag priority={priority} /> : formatPriorityLabel(priority) },
    { label: 'Complexity', value: text(estimatedComplexity) },
    { label: 'Requested', value: createdAt ? formatDate(createdAt) || null : null },
  ];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 360px' }, gap: 2.5, alignItems: 'start' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
        <Section title="Project brief">
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: brief ? 'text.primary' : 'text.secondary', overflowWrap: 'anywhere' }}>
            {brief || 'The creator did not write a description.'}
          </Typography>
        </Section>

        {notes && (
          <Section title={`Notes from ${creatorName}`}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {notes}
            </Typography>
          </Section>
        )}

        <Section title="Creative direction">
          <KeyValueList items={direction} />
        </Section>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
        <Section title="Schedule">
          <KeyValueList items={schedule} columns={1} />
        </Section>
        {references.length > 0 && (
          <Section title="References">
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 0.75 }}>
              {references.map((link) => (
                <li key={link}>
                  <Link href={link} target="_blank" rel="noopener noreferrer" sx={{ overflowWrap: 'anywhere' }}>
                    {link}
                  </Link>
                </li>
              ))}
            </Box>
          </Section>
        )}
      </Box>
    </Box>
  );
};

export default BriefTab;
